//TemplatesList.js

import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  IconButton,
  Tooltip,
  Checkbox,
  Switch,
  Grid,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import EditTemplate from "./EditTemplate";
import AddTemplate from "./AddTemplate";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface TemplateData {
  template_id: number;
  template_name: string;
  template_subject: string;
  template_content: string;
  asset_type_id: number;
  asset_type_name: string;
}

interface SelectedRow {
  dataIndex: number;
}

interface ColumnMeta {
  label?: string;
  name: string;
  index: number;
}

interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: TemplateData[];
}

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface Template {
  template_name: string;
  template_subject: string;
  template_content: string;
  asset_type_id: number;
  asset_type_name: string;
  template_id: number;
}

// Extracted component for checkbox header
interface CheckboxHeaderProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const CheckboxHeader: React.FC<CheckboxHeaderProps> = ({
  checked,
  onChange,
}) => {
  return (
    <th
      style={{
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <Checkbox
        checked={checked}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.checked)
        }
      />
    </th>
  );
};

// Extracted component for checkbox body
interface CheckboxBodyProps {
  checked: boolean;
  onChange: () => void;
}

const CheckboxBody: React.FC<CheckboxBodyProps> = ({ checked, onChange }) => {
  return <Checkbox checked={checked} onChange={onChange} />;
};

// Extracted component for content cell
interface ContentCellProps {
  content: string;
  parseContent: (content: string) => string;
}

const ContentCell: React.FC<ContentCellProps> = ({ content, parseContent }) => {
  const parsedContent = parseContent(content);
  const displayValue =
    parsedContent?.length > 20
      ? `${parsedContent.substring(0, 30)}...`
      : parsedContent;

  return (
    <Tooltip title={parsedContent}>
      <span>{displayValue}</span>
    </Tooltip>
  );
};

// Extracted component for status switch
interface StatusSwitchProps {
  checked: boolean;
  onStatusChange: (isActive: boolean, data: TemplateData) => void;
  data: TemplateData;
}

const StatusSwitch: React.FC<StatusSwitchProps> = ({
  checked,
  onStatusChange,
  data,
}) => {
  return (
    <Switch
      checked={checked}
      onChange={(event) => onStatusChange(event.target.checked, data)}
      color="primary"
    />
  );
};

// Extracted component for action buttons
interface ActionButtonsProps {
  onEdit: (data: TemplateData) => void;
  data: TemplateData;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ onEdit, data }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <IconButton aria-label="edit" onClick={() => onEdit(data)}>
        <EditIcon />
      </IconButton>
    </Box>
  );
};

const TemplatesList = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [rows, setRows] = React.useState<TemplateData[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [isEditOpen, setEditOpen] = React.useState(false);
  const [isAddOpen, setAddOpen] = React.useState(false);
  const [templateData, setTemplateData] = React.useState<Template | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<SelectedRow[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeItem[]>([]);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);

  const parseContent = (content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    return doc.body.textContent || "";
  };

  const handleEditOpen = (row: TemplateData) => {
    const formatted: Template = {
      template_id: row.template_id,
      template_name: row.template_name,
      template_subject: row.template_subject,
      template_content: row.template_content,
      asset_type_id: row.asset_type_id,
      asset_type_name: row.asset_type_name,
    };

    setTemplateData(formatted);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleAddOpen = () => {
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
  };

  const handleStatus = async (
    isActive: boolean,
    data: { template_id: number }
  ) => {
    try {
      await axios.put(`/email-templates/${data?.template_id}`, {
        is_active: isActive,
      });
      fetchData();
      fetchSuccess("Status updated successfully");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleBulkStatusUpdate = async (isActive: boolean): Promise<void> => {
    if (!rows) return;
    try {
      const promises = selectedRows.map(async (row) => {
        const template = rows[row.dataIndex];
        return axios.put(`/email-templates/${template.template_id}`, {
          is_active: isActive,
        });
      });
      await Promise.all(promises);
      setSelectedRows([]);
      fetchData();
      fetchSuccess(isActive ? "Templates activated" : "Templates deactivated");
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  const handleCheckAllboxes = (checked: boolean): void => {
    if (!rows) return;

    setSelectedRows(
      checked ? rows.map((_, index) => ({ dataIndex: index })) : []
    );
  };

  const handleCheckboxChange = (rowIndex: number): void => {
    setSelectedRows((prevSelectedRows) => {
      const isSelected = prevSelectedRows.some(
        (row) => row.dataIndex === rowIndex
      );
      return isSelected
        ? prevSelectedRows.filter((row) => row.dataIndex !== rowIndex)
        : [...prevSelectedRows, { dataIndex: rowIndex }];
    });
  };

  // Fixed: Render functions for custom cells
  const renderCheckboxHeader = (columnMeta: ColumnMeta) => {
    return (
      <CheckboxHeader
        checked={
          !!rows && rows.length > 0 && selectedRows.length === rows.length
        }
        onChange={handleCheckAllboxes}
      />
    );
  };

  const renderCheckboxBody = (value: unknown, tableMeta: TableMeta) => {
    return (
      <CheckboxBody
        checked={selectedRows.some(
          (row) => row.dataIndex === tableMeta.rowIndex
        )}
        onChange={() => handleCheckboxChange(tableMeta.rowIndex)}
      />
    );
  };

  const renderContentCell = (value: string) => {
    return <ContentCell content={value} parseContent={parseContent} />;
  };

  const renderStatusSwitch = (value: boolean, tableMeta: TableMeta) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];

    return (
      <StatusSwitch checked={value} onStatusChange={handleStatus} data={data} />
    );
  };

  const renderActionButtons = (value: unknown, tableMeta: TableMeta) => {
    const { currentTableData, rowIndex } = tableMeta;
    const data = tableMeta?.tableData[currentTableData[rowIndex]?.index];

    return <ActionButtons onEdit={handleEditOpen} data={data} />;
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: "checkbox",
      label: "",
      options: {
        filter: false,
        sort: false,
        customHeadRender: renderCheckboxHeader,
        customBodyRender: renderCheckboxBody,
      },
    },
    { name: "template_name", label: "Name" },
    { name: "template_subject", label: "Subject" },
    {
      name: "template_content",
      label: "Content",
      options: {
        filter: false,
        customBodyRender: renderContentCell,
      },
    },
    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusSwitch,
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionButtons,
      },
    },
  ];

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/email-templates", {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
          },
        },
      });

      setRows(response?.data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter]);

  React.useEffect(() => {
    fetchData();
  }, [assetTypeFilter, fetchData]);

  const fetchAssets = async () => {
    try {
      const assetTypesData = await axios.get("/asset-types");
      setAssetTypes(assetTypesData?.data?.list);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchAssets();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <div style={{ display: "block", justifyContent: "space-between" }}>
          <SoftButton variant="gradient" color="info" onClick={handleAddOpen}>
            Add Template
          </SoftButton>

          <div
            style={{
              display: "block",
              justifyContent: "flex-end",
              marginLeft: "10px",
              float: "right",
            }}
          >
            <Grid style={{ float: "left" }}>
              <FormControl sx={{ paddingRight: "10px" }}>
                <Autocomplete
                  options={assetTypes || []}
                  getOptionLabel={(option) => option.asset_type_name}
                  value={assetTypeFilter}
                  onChange={(event, value) => {
                    setAssetTypeFilter(value);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Asset Type"
                      sx={{
                        "& label": {
                          fontSize: "14px",
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Button
              variant="contained"
              className="success-btn"
              onClick={() => {
                handleBulkStatusUpdate(true);
              }}
              disabled={selectedRows.length === 0}
              sx={{ marginRight: "10px", marginBottom: "10px" }}
            >
              Activate
            </Button>
            <Button
              variant="contained"
              className="success-btn"
              onClick={() => {
                handleBulkStatusUpdate(false);
              }}
              disabled={selectedRows.length === 0}
              sx={{ marginBottom: "10px" }}
            >
              InActivate
            </Button>
          </div>
        </div>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-lifts"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Templates"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
      {isEditOpen && (
        <EditTemplate
          isOpen={isEditOpen}
          onClose={handleEditClose}
          templateData={templateData}
          fetchData={fetchData}
        />
      )}
      {isAddOpen && (
        <AddTemplate
          isOpen={isAddOpen}
          onClose={handleAddClose}
          fetchData={fetchData}
        />
      )}
    </DashboardLayout>
  );
};

export default TemplatesList;
